import { Path } from 'fabric';
import {
  calculateDistance,
  isNearPoint,
  applyHierarchicalSnap,
  ENDPOINT_SNAP_DISTANCE,
} from './snapUtils';
import { GARDEN_OBJECT_TYPES, createGardenObject } from '../../../constants/gardenObjectTypes';
import { getObjectTypeById } from '../../../config';

/**
 * BezierTool - Handles Bezier curve drawing logic for organic garden shapes
 *
 * Features:
 * - Click-to-add-control-points workflow (multi-click, not drag)
 * - Cubic Bezier curves (4 control points minimum)
 * - Snap-to-grid support for control points (via snapUtils)
 * - Snap-to-endpoint support for control points (via snapUtils)
 * - Auto-close when clicking near first point (10px tolerance)
 * - Enter key to close curve
 * - Minimum 4 control points required (cubic Bezier)
 * - Visual feedback: control point markers, connecting curves, live preview
 * - Properties panel: vertices, area, perimeter, label (NO stroke color/width)
 *
 * Design based on: Issue #68 and Polygon tool patterns
 */

// Re-export snap utilities for backward compatibility
export {
  calculateDistance,
  isNearPoint,
  applyHierarchicalSnap,
  ENDPOINT_SNAP_DISTANCE,
};

/**
 * Convert array of points to SVG path string using Catmull-Rom spline
 *
 * Creates smooth curves that pass THROUGH all the clicked points,
 * not treating them as off-curve control points. This is more intuitive
 * for users who just want to click points and have a smooth curve.
 *
 * Algorithm: Converts Catmull-Rom spline to cubic Bezier segments
 * - Each segment uses points: P[i-1], P[i], P[i+1], P[i+2]
 * - Control points are calculated to create smooth transitions
 *
 * @param {Array<{x: number, y: number}>} points - Points the curve should pass through
 * @param {boolean} closePath - Whether to close the path (connect to first point)
 * @returns {string} SVG path data string
 */
export const pointsToBezierPath = (points, closePath = false) => {
  if (!points || points.length < 2) {
    throw new Error('Bezier curve requires at least 2 points');
  }

  // For 2-3 points, just draw lines
  if (points.length < 4) {
    let pathData = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      pathData += ` L ${points[i].x} ${points[i].y}`;
    }
    if (closePath) {
      pathData += ' Z';
    }
    return pathData;
  }

  // Catmull-Rom tension parameter (0 = sharp, 0.5 = standard, 1 = loose)
  const tension = 0.5;

  // For closed paths, we need to wrap points for continuous curve
  let workingPoints = [...points];
  if (closePath) {
    // Add points to wrap around for smooth closure
    workingPoints = [
      points[points.length - 1], // Wrap: last point before first
      ...points,
      points[0], // Wrap: first point after last
      points[1], // Wrap: second point for smooth closure
    ];
  } else {
    // For open paths, duplicate first and last points
    workingPoints = [
      points[0], // Duplicate first
      ...points,
      points[points.length - 1], // Duplicate last
    ];
  }

  // Start path at first actual point
  const startIndex = closePath ? 1 : 1;
  let pathData = `M ${workingPoints[startIndex].x} ${workingPoints[startIndex].y}`;

  // Generate cubic Bezier segments from Catmull-Rom spline
  const numSegments = closePath ? points.length : points.length - 1;

  for (let i = 0; i < numSegments; i++) {
    const p0 = workingPoints[i];
    const p1 = workingPoints[i + 1];
    const p2 = workingPoints[i + 2];
    const p3 = workingPoints[i + 3];

    // Calculate cubic Bezier control points from Catmull-Rom points
    // Using the standard conversion formula
    const cp1 = {
      x: p1.x + (p2.x - p0.x) * tension / 3,
      y: p1.y + (p2.y - p0.y) * tension / 3,
    };

    const cp2 = {
      x: p2.x - (p3.x - p1.x) * tension / 3,
      y: p2.y - (p3.y - p1.y) * tension / 3,
    };

    pathData += ` C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${p2.x} ${p2.y}`;
  }

  // Close path if requested
  if (closePath) {
    pathData += ' Z';
  }

  return pathData;
};

/**
 * Calculate Bezier path with hierarchical snapping applied
 *
 * Applies snap-to-endpoint and snap-to-grid to each control point
 * before generating the SVG path string.
 *
 * @param {Array<{x: number, y: number}>} points - Raw control points
 * @param {object} canvas - Fabric.js canvas instance
 * @param {number} gridSize - Grid size in pixels (e.g., 100 for 1m)
 * @param {boolean} snapEnabled - Whether snap-to-grid is enabled
 * @param {boolean} ctrlPressed - Whether Ctrl key is pressed (disables grid snap)
 * @param {boolean} closePath - Whether to close the path
 * @returns {string} SVG path data string with snapped coordinates
 */
export const calculateBezierPath = (
  points,
  canvas,
  gridSize,
  snapEnabled,
  ctrlPressed,
  closePath = false
) => {
  if (!points || points.length < 2) {
    throw new Error('Bezier curve requires at least 2 points');
  }

  // Apply hierarchical snap to each point
  const snappedPoints = points.map((point) =>
    applyHierarchicalSnap(
      point,
      canvas,
      gridSize,
      snapEnabled,
      ctrlPressed,
      ENDPOINT_SNAP_DISTANCE
    )
  );

  return pointsToBezierPath(snappedPoints, closePath);
};

/**
 * Create a Fabric.js Path object for a Bezier curve
 *
 * Uses default styling consistent with garden planner design:
 * - Active (selected): Emerald-500 stroke + light fill
 * - Inactive: Gray-900 stroke + light fill
 *
 * @param {string} pathData - SVG path data string
 * @param {object} options - Optional Fabric.js Path options
 * @returns {Path} Fabric.js Path object
 */
export const createBezierCurve = (pathData, options = {}, objectTypeId = null) => {
  // Get default style from config if objectTypeId provided
  let strokeColor = '#10B981'; // Fallback to emerald-500
  let strokeWidth = 2;
  let fillColor = 'rgba(16, 185, 129, 0.1)'; // Light emerald fill
  let opacity = 1;
  let objectConfig = null;

  if (objectTypeId) {
    objectConfig = getObjectTypeById(objectTypeId);
    if (objectConfig && objectConfig.defaultStyle) {
      const style = objectConfig.defaultStyle;
      strokeColor = style.stroke || strokeColor;
      strokeWidth = style.strokeWidth || strokeWidth;
      fillColor = style.fill || fillColor;
      opacity = style.opacity !== undefined ? style.opacity : 1;
    }
  }

  const bezierCurve = new Path(pathData, {
    originX: 'left',
    originY: 'top',
    selectable: true,
    evented: true,
    objectCaching: true,
    strokeUniform: true, // Keep stroke width constant during scaling
    perPixelTargetFind: true, // Pixel-accurate hit detection (not bounding box)
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    fill: fillColor,
    opacity: opacity,
    ...options,
  });

  // Apply garden object type system (won't override colors now due to fix)
  createGardenObject(GARDEN_OBJECT_TYPES.BEZIER_CURVE, bezierCurve, {
    data: {
      shapeType: 'bezier', // Renamed from 'type' to avoid conflict with garden object properties
      isActive: true,
      objectTypeId: objectTypeId,
      gardenObjectType: objectTypeId, // Add both for compatibility
      createdAt: new Date().toISOString(),
      // Store original colors for selection/deselection
      originalStroke: strokeColor,
      originalStrokeWidth: strokeWidth,
      originalFill: fillColor,
      originalOpacity: opacity,
      // Add config metadata if available
      ...(objectConfig && {
        isPlantable: objectConfig.isPlantable,
        castsShadow: objectConfig.castsShadow,
      }),
    },
  });

  return bezierCurve;
};

/**
 * Update Bezier curve appearance based on selection state
 *
 * Active (selected): Emerald-500 stroke
 * Inactive: Restore original config colors
 *
 * @param {Path} curve - Fabric.js Path object
 * @param {boolean} isActive - Whether curve is selected/active
 */
export const updateBezierAppearance = (curve, isActive) => {
  if (!curve || curve.type !== 'path') {
    throw new Error('Invalid Bezier curve object');
  }

  if (!curve.data) return;

  if (isActive) {
    // Selected: Use emerald-500 green
    curve.set({
      stroke: '#10B981',
      fill: 'rgba(16, 185, 129, 0.1)',
    });
    if (curve.data) {
      curve.data.isActive = true;
    }
  } else {
    // Deselected: Restore original config colors from object type definition
    const objectTypeId = curve.data.objectTypeId || curve.data.gardenObjectType;

    if (objectTypeId) {
      // Get config for this object type
      const objectConfig = getObjectTypeById(objectTypeId);

      if (objectConfig && objectConfig.defaultStyle) {
        // Restore colors from config
        curve.set({
          stroke: objectConfig.defaultStyle.stroke || '#6B7280',
          strokeWidth: objectConfig.defaultStyle.strokeWidth || 2,
          fill: objectConfig.defaultStyle.fill || 'rgba(107, 114, 128, 0.1)',
          opacity: objectConfig.defaultStyle.opacity !== undefined ? objectConfig.defaultStyle.opacity : 1,
        });
      } else {
        // Fallback to neutral colors if no config found
        curve.set({
          stroke: '#6B7280',
          strokeWidth: 2,
          fill: 'rgba(107, 114, 128, 0.1)',
          opacity: 1,
        });
      }
    } else {
      // No object type (legacy or Zeichnen tab usage) - use neutral colors
      curve.set({
        stroke: '#6B7280',
        strokeWidth: 2,
        fill: 'rgba(107, 114, 128, 0.1)',
        opacity: 1,
      });
    }

    if (curve.data) {
      curve.data.isActive = false;
    }
  }

  curve.canvas?.requestRenderAll();
};

/**
 * Get Bezier curve properties (for properties panel)
 *
 * Extracts control points from SVG path data and calculates
 * area, perimeter, and other properties.
 *
 * @param {Path} curve - Fabric.js Path object
 * @returns {object} Bezier curve properties
 */
export const getBezierProperties = (curve) => {
  if (!curve || curve.type !== 'path') {
    throw new Error('Invalid Bezier curve object');
  }

  // Extract control points from path data
  const points = extractControlPointsFromPath(curve.path);

  // Calculate area using shoelace formula (same as polygon)
  const area = calculatePolygonArea(points);

  // Calculate perimeter (sum of edge lengths)
  const perimeter = calculatePolygonPerimeter(points);

  return {
    points,
    controlPointCount: points.length,
    area,
    perimeter,
    label: curve.data?.label || '',
    left: curve.left || 0,
    top: curve.top || 0,
    pathData: curve.path,
  };
};

/**
 * Set Bezier curve properties (from properties panel)
 *
 * Updates control points, label, and position.
 * Regenerates path data if points are modified.
 *
 * @param {Path} curve - Fabric.js Path object
 * @param {object} properties - Properties to update
 */
export const setBezierProperties = (curve, properties) => {
  if (!curve || curve.type !== 'path') {
    throw new Error('Invalid Bezier curve object');
  }

  if (properties.label !== undefined) {
    curve.set({
      data: {
        ...curve.data,
        label: properties.label,
      },
    });
  }

  if (properties.points) {
    // Regenerate path data from new points
    const pathData = pointsToBezierPath(properties.points, true);
    curve.set({ path: pathData });
  }

  if (properties.left !== undefined) {
    curve.set({ left: properties.left });
  }

  if (properties.top !== undefined) {
    curve.set({ top: properties.top });
  }

  curve.setCoords();
  curve.canvas?.requestRenderAll();
};

/**
 * Extract control points from Fabric.js path data
 *
 * Parses SVG path commands to extract {x, y} coordinates.
 * Handles M (move), C (cubic Bezier), L (line), and Z (close) commands.
 *
 * @param {Array} pathData - Fabric.js path data array
 * @returns {Array<{x: number, y: number}>} Control points
 */
export const extractControlPointsFromPath = (pathData) => {
  if (!pathData || !Array.isArray(pathData)) {
    return [];
  }

  const points = [];

  pathData.forEach((command) => {
    const type = command[0];

    switch (type) {
      case 'M': // Move to (start point)
        points.push({ x: command[1], y: command[2] });
        break;

      case 'C': // Cubic Bezier (3 points: cp1, cp2, end)
        points.push(
          { x: command[1], y: command[2] }, // Control point 1
          { x: command[3], y: command[4] }, // Control point 2
          { x: command[5], y: command[6] }  // End point
        );
        break;

      case 'L': // Line to
        points.push({ x: command[1], y: command[2] });
        break;

      case 'Q': // Quadratic Bezier (2 points: cp, end)
        points.push(
          { x: command[1], y: command[2] }, // Control point
          { x: command[3], y: command[4] }  // End point
        );
        break;

      case 'Z': // Close path (no coordinates)
        break;

      default:
        console.warn(`Unknown path command: ${type}`);
    }
  });

  return points;
};

/**
 * Calculate area of polygon using shoelace formula
 *
 * Same algorithm as PolygonTool - treats Bezier curve as closed polygon
 * for area calculation purposes.
 *
 * @param {Array<{x: number, y: number}>} vertices - Polygon vertices
 * @returns {number} Area in square pixels
 */
export const calculatePolygonArea = (vertices) => {
  if (!vertices || vertices.length < 3) return 0;

  let area = 0;
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length;
    area += vertices[i].x * vertices[j].y;
    area -= vertices[j].x * vertices[i].y;
  }

  return Math.abs(area / 2);
};

/**
 * Calculate perimeter of polygon (sum of edge lengths)
 *
 * Same algorithm as PolygonTool - sums distances between consecutive vertices.
 *
 * @param {Array<{x: number, y: number}>} vertices - Polygon vertices
 * @returns {number} Perimeter in pixels
 */
export const calculatePolygonPerimeter = (vertices) => {
  if (!vertices || vertices.length < 2) return 0;

  let perimeter = 0;
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length;
    perimeter += calculateDistance(vertices[i], vertices[j]);
  }

  return perimeter;
};

/**
 * Validate Bezier curve path data
 *
 * Checks if path data string is valid for Bezier curves.
 * Must contain at least one cubic Bezier segment (M + C commands).
 *
 * @param {string} pathData - SVG path data string
 * @returns {boolean} True if valid Bezier path
 */
export const isValidBezierCurve = (pathData) => {
  if (!pathData || typeof pathData !== 'string') {
    return false;
  }

  // Must start with M (move to)
  if (!pathData.startsWith('M')) {
    return false;
  }

  // Must contain at least one C (cubic Bezier) command
  if (!pathData.includes('C')) {
    return false;
  }

  return true;
};

/**
 * Get Bezier curve control points from path data
 *
 * Convenience function to extract just the control points
 * without full property data.
 *
 * @param {Path} curve - Fabric.js Path object
 * @returns {Array<{x: number, y: number}>} Control points
 */
export const getBezierControlPoints = (curve) => {
  if (!curve || curve.type !== 'path') {
    throw new Error('Invalid Bezier curve object');
  }

  return extractControlPointsFromPath(curve.path);
};
